import { fetchDivisions } from '@/services/divisionsService'
import { fetchMenuCategories } from '@/services/menuService'
import {
    fetchDivisionWorkshops,
    bulkUpdateMenuItemWorkshops,
    fetchMenuItemWorkshops,
    fetchMenuItemNoPrint,
    batchSetMenuItemNoPrint,
    batchDeleteMenuItemNoPrint,
} from '@/services/printManagementService'
import {
    addItemsToStopListBatch,
    deleteItemsFromStopListBatch,
    fetchDivisionMenu,
    fetchStopListReasons,
} from '@/services/stopListService'
import { notificationService } from '@/utils/notifications'

import { buildEndDatetime } from './utils/date'
import { processMenuItems } from './utils/menuProcessor'
import { enrichItemsWithNoPrint } from './utils/noPrintEnricher'
import { getSuccessText } from './utils/notifications'
import { enrichItemsWithPrintLocations } from './utils/printLocationEnricher'

export default {
    /**
     * Загружает список блюд/категорий.
     * Решает, использовать поиск или выборку по категории.
     */
    async fetchItems({ commit, state }) {
        const { divisionId, categoryId, search, status } = state.filters

        // Если не выбран ресторан, ничего не делаем
        if (!divisionId) {
            commit('SET_ITEMS', [])
            commit('SET_LAST_KNOWN_STOPPED_COUNT', 0)
            return
        }

        commit('SET_LOADING', true)
        try {
            let response

            if (search) {
                response = await fetchDivisionMenu(divisionId, {
                    query: search,
                    filter: status,
                })
            } else if (categoryId) {
                response = await fetchDivisionMenu(divisionId, {
                    categoryId,
                    filter: status,
                })
            } else {
                commit('SET_ITEMS', [])
                return
            }

            // Получаем сырые данные
            const rawElements = response?.data?.elements || []

            // Вызываем нашу утилиту для обработки данных
            const processedElements = processMenuItems(
                rawElements,
                state.filters
            )

            if (state.filters.status !== 'not_in_stoplist') {
                const stoppedCountInList = processedElements.filter(
                    item => item.type === 'dish' && !!item.stop_info
                ).length
                commit('SET_LAST_KNOWN_STOPPED_COUNT', stoppedCountInList)
            }

            // Обогащаем блюда местами печати из уже загруженных привязок
            const enrichedElements = enrichItemsWithPrintLocations(
                processedElements,
                state.printManagement.workshopMappings,
                state.printManagement.printLocations
            )

            // Обогащаем блюда признаком отключённой печати
            const enrichedWithNoPrint = enrichItemsWithNoPrint(
                enrichedElements,
                state.printManagement.noPrintItemIds
            )

            commit('SET_ITEMS', enrichedWithNoPrint)
        } catch (error) {
            console.error('Ошибка загрузки блюд', error)
            commit('SET_ITEMS', [])
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async fetchDivisions({ commit }) {
        try {
            const response = await fetchDivisions({ mod: 'short' })
            commit('SET_DIVISIONS', response.data)
        } catch (error) {
            console.error('Ошибка загрузки ресторанов', error)
            notificationService.showError(error)
        }
    },

    async addSelectedToStopList({ dispatch, state }, formData) {
        // 1. Получаем сырые данные из компонента
        const { reasonId, timeMode, duration, date } = formData
        // 2. Action берет на себя ответственность за подготовку данных для API
        const end_datetime = buildEndDatetime(timeMode, duration, date)

        // 3. Формируем payload для API
        const selectedItemsFull = state.selectedItems

        const categories = selectedItemsFull.filter(
            item => item.type === 'category'
        )

        // Если есть исключения, отправляем пустой массив `menu_items`.
        // Иначе, отправляем индивидуально выбранные блюда.
        const menuItems =
            state.excludedItemIds.length > 0
                ? []
                : selectedItemsFull.filter(
                      item =>
                          item.type === 'dish' &&
                          !selectedItemsFull.some(
                              sel => sel.id === item.parent_id
                          )
                  )

        const payload = {
            division_id: state.filters.divisionId,
            reason_id: reasonId,
            start_datetime: new Date().toISOString(),
            end_datetime,
            categories: categories.map(item => item.id),
            exclusions: state.excludedItemIds,

            menu_items: menuItems.map(item => item.id),
        }

        try {
            await addItemsToStopListBatch(payload)

            notificationService.showSuccess(
                getSuccessText(categories, menuItems, 'add')
            )

            dispatch('clearSelection')
            await dispatch('fetchItems')
        } catch (error) {
            notificationService.showError(error)
            throw error
        }
    },

    async removeSelectedFromStopList({ dispatch, state }) {
        const selectedItemsFull = state.selectedItems

        const menuItems = selectedItemsFull.filter(
            item => !item.type || item.type === 'dish'
        )

        const menuItemsForMessage = menuItems.filter(
            item => !selectedItemsFull.some(sel => sel.id === item.parent_id)
        )

        const categories = selectedItemsFull.filter(
            item => item.type === 'category'
        )

        const payload = {
            division_id: state.filters.divisionId,
            menu_items: menuItems.map(item => item.id),
            categories: categories.map(item => item.id),

            delete_all: false,
        }
        try {
            const result = await deleteItemsFromStopListBatch(payload)
            notificationService.showSuccess(
                getSuccessText(categories, menuItemsForMessage, 'remove')
            )
            dispatch('clearSelection')
            await dispatch('fetchItems')

            return result
        } catch (e) {
            notificationService.showError(e)
            throw e
        }
    },

    async removeAllFromStopList({ dispatch, state }) {
        const payload = {
            division_id: state.filters.divisionId,
            delete_all: true,
        }

        const result = await deleteItemsFromStopListBatch(payload)

        dispatch('clearSelection')
        await dispatch('fetchItems')

        return result
    },

    /**
     * Переключает выделение элемента (блюда/категории/все).
     * Управляет сложной логикой выбора с учетом:
     * - Групповых операций с категориями
     * - Исключений внутри выбранных категорий
     * - Конфликтов статусов (в стоп-листе / не в стоп-листе)
     *
     * @param {Object} context - Vuex action context
     * @param {Object} itemToToggle - Элемент для переключения выделения
     * @param {string} itemToToggle.type - Тип: 'all' | 'category' | 'dish'
     * @param {string} itemToToggle.id - ID элемента
     */
    toggleSelection({ commit, state }, itemToToggle) {
        const allItemsInList = [...state.items]
        let newSelection = [...state.selectedItems]
        let newExclusions = [...state.excludedItemIds]

        const isSelected = item => newSelection.some(i => i.id === item.id)

        switch (itemToToggle.type) {
            // --- СЦЕНАРИЙ: 'ВСЯ КАТЕГОРИЯ' ---
            case 'all': {
                newExclusions = [] // При выборе всех, исключений быть не может
                if (newSelection.length === allItemsInList.length) {
                    newSelection = [] // Снимаем выделение, если все уже выбрано
                } else {
                    newSelection = [...allItemsInList] // Выбираем все видимые
                }
                break
            }

            // --- СЦЕНАРИЙ: ОБЫЧНАЯ КАТЕГОРИЯ ---
            case 'category': {
                const childDishes = allItemsInList.filter(
                    item =>
                        item.parent_id === itemToToggle.id &&
                        item.type === 'dish'
                )
                const groupItems = [itemToToggle, ...childDishes]
                const groupIds = groupItems.map(item => item.id)

                const areAllInGroupSelected = groupItems.every(item =>
                    isSelected(item)
                )

                if (areAllInGroupSelected) {
                    // СНЯТИЕ ВЫДЕЛЕНИЯ с группы: просто убираем их
                    newSelection = newSelection.filter(
                        item => !groupIds.includes(item.id)
                    )
                    newSelection = newSelection.filter(
                        item => item.id !== 'all-category-pseudo-id'
                    )
                } else {
                    // ДОБАВЛЕНИЕ группы в выбор
                    const itemsToAdd = groupItems.filter(
                        item => !isSelected(item)
                    )

                    if (
                        newSelection.length > 0 &&
                        !!newSelection[0].stop_info !==
                            !!itemsToAdd[0].stop_info
                    ) {
                        // Конфликт статусов! Сбрасываем старый выбор и начинаем новый с этой группы.
                        newSelection = itemsToAdd
                        newExclusions = []
                    } else {
                        // Конфликта нет, просто добавляем
                        newSelection.push(...itemsToAdd)
                    }
                }
                break
            }

            // --- СЦЕНАРИЙ: БЛЮДО ---
            case 'dish': {
                const parentCategory = allItemsInList.find(
                    i => i.id === itemToToggle.parent_id
                )
                const isParentSelected =
                    parentCategory && isSelected(parentCategory)

                if (isParentSelected) {
                    // Работа с исключениями внутри уже выбранной категории (конфликта статусов быть не может)
                    const isExcluded = newExclusions.includes(itemToToggle.id)
                    if (isExcluded) {
                        newExclusions = newExclusions.filter(
                            id => id !== itemToToggle.id
                        )
                        newSelection.push(itemToToggle) // Возвращаем в выбор
                    } else {
                        newExclusions.push(itemToToggle.id)
                        newSelection = newSelection.filter(
                            sel => sel.id !== itemToToggle.id
                        ) // Убираем из выбора
                    }
                } else if (isSelected(itemToToggle)) {
                    // СНЯТИЕ ВЫДЕЛЕНИЯ с одиночного блюда
                    newSelection = newSelection.filter(
                        sel => sel.id !== itemToToggle.id
                    )
                    newSelection = newSelection.filter(
                        item => item.id !== 'all-category-pseudo-id'
                    )
                } else {
                    // ДОБАВЛЕНИЕ одиночного блюда
                    if (
                        newSelection.length > 0 &&
                        !!newSelection[0].stop_info !== !!itemToToggle.stop_info
                    ) {
                        // Конфликт! Сбрасываем и выбираем только этот элемент
                        newSelection = [itemToToggle]
                        newExclusions = []
                    } else {
                        // Конфликта нет, добавляем
                        newSelection.push(itemToToggle)
                    }
                }
                break
            }
            default:
                break
        }

        // Единственное место, где мы меняем состояние. Чисто и предсказуемо.
        commit('SET_SELECTED_ITEMS', newSelection)
        commit('SET_EXCLUDED_ITEM_IDS', newExclusions)
    },

    // Очищает выделение
    clearSelection({ commit }) {
        commit('SET_SELECTED_ITEMS', [])
        commit('SET_EXCLUDED_ITEM_IDS', [])
    },

    applyFilters({ commit, dispatch }, payload = {}) {
        const filterFields = ['divisionId', 'status', 'search', 'categoryId']
        const updates = []

        if (payload.name && filterFields.includes(payload.name)) {
            updates.push({ name: payload.name, value: payload.value })
        }

        filterFields.forEach(field => {
            if (
                field in payload &&
                (field !== payload.name || payload.value !== payload[field])
            ) {
                updates.push({ name: field, value: payload[field] })
            }
        })

        if (!updates.length) {
            return
        }

        updates.forEach(({ name, value }) => {
            if (name === 'divisionId') {
                commit('SET_FILTERS', { name: 'categoryId', value: null })
                commit('SET_LAST_KNOWN_STOPPED_COUNT', 0)
                // Сброс кэшей печати, чтобы не обогащать блюда данными
                // предыдущего подразделения при переключении
                commit('SET_PRINT_LOCATIONS', [])
                commit('SET_WORKSHOP_MAPPINGS', [])
                commit('SET_NO_PRINT_ITEM_IDS', [])
                commit('SET_SELECTED_PRINT_LOCATION_IDS', [])
            }
            commit('SET_FILTERS', { name, value })
        })

        commit('SET_SELECTED_ITEMS', [])
        dispatch('fetchItems')
    },

    async fetchCategories({ commit, state }) {
        const { divisionId } = state.filters
        if (!divisionId) {
            commit('SET_CATEGORIES', [])
            return
        }

        commit('SET_CATEGORIES_LOADING', true)
        try {
            const response = await fetchMenuCategories(divisionId)
            commit('SET_CATEGORIES', response.data?.categories || [])
        } catch (error) {
            console.error('Ошибка загрузки категорий меню', error)
            notificationService.showError(error)
        } finally {
            commit('SET_CATEGORIES_LOADING', false)
        }
    },

    async fetchReasons({ commit, state }) {
        if (state.reasons && state.reasons.length > 0) {
            return
        }

        try {
            const response = await fetchStopListReasons()
            commit('SET_REASONS', response.data.items)
        } catch (error) {
            console.error('Failed to load stop-list reasons', error)
            notificationService.showError(error)
        }
    },

    // ═══════════════════════════════════════════════
    // ПЕЧАТЬ БЛЮД — Actions
    // ═══════════════════════════════════════════════

    async fetchPrintLocations({ commit, state }) {
        if (state.printManagement.isPrintLocationsLoading) return

        const { divisionId } = state.filters
        if (!divisionId) {
            commit('SET_PRINT_LOCATIONS', [])
            commit('SET_WORKSHOP_MAPPINGS', [])
            commit('SET_NO_PRINT_ITEM_IDS', [])
            return
        }

        commit('SET_PRINT_LOCATIONS_LOADING', true)
        try {
            // Загружаем места печати (workshops)
            const workshopsResponse = await fetchDivisionWorkshops(divisionId)
            commit('SET_PRINT_LOCATIONS', workshopsResponse.data || [])

            // Загружаем ВСЕ привязки блюд к местам печати для всего подразделения
            const mappingsResponse = await fetchMenuItemWorkshops(divisionId)
            commit('SET_WORKSHOP_MAPPINGS', mappingsResponse.data || [])
        } catch (error) {
            console.error('Ошибка загрузки мест печати', error)
            notificationService.showError(error)
            commit('SET_PRINT_LOCATIONS', [])
            commit('SET_WORKSHOP_MAPPINGS', [])
        } finally {
            commit('SET_PRINT_LOCATIONS_LOADING', false)
        }
    },

    async fetchItemsWithPrintInfo({ commit, state, dispatch }) {
        const { divisionId } = state.filters

        if (!divisionId) {
            commit('SET_ITEMS', [])
            return
        }

        try {
            // Check loading flag to prevent race condition
            if (
                !state.printManagement.isPrintLocationsLoading &&
                (state.printManagement.printLocations.length === 0 ||
                    state.printManagement.workshopMappings.length === 0)
            ) {
                await dispatch('fetchPrintLocations')
            }

            await dispatch('fetchNoPrintItems')
            await dispatch('fetchItems')
        } catch (error) {
            console.error('Error loading print management data', error)
            notificationService.showError(error)
            commit('SET_ITEMS', [])
        }
    },

    async fetchNoPrintItems({ commit, state }) {
        const { divisionId } = state.filters
        if (!divisionId) {
            commit('SET_NO_PRINT_ITEM_IDS', [])
            return
        }

        try {
            const response = await fetchMenuItemNoPrint(divisionId)
            commit(
                'SET_NO_PRINT_ITEM_IDS',
                response?.data?.menu_item_ids || []
            )
        } catch (error) {
            console.error('Ошибка загрузки отключённых печатей', error)
            notificationService.showError(error)
            commit('SET_NO_PRINT_ITEM_IDS', [])
        }
    },

    async toggleSelectedItemsPrintStatus({ dispatch, state, commit }, { disable }) {
        const selectedItemsFull = state.selectedItems

        const categoryIds = new Set(
            selectedItemsFull
                .filter(item => item.type === 'category')
                .map(item => item.id)
        )

        // Блюда: выбранные напрямую + дочерние выбранных категорий
        const menuItemIds = selectedItemsFull
            .filter(item => !item.type || item.type === 'dish')
            .map(item => item.id)

        const allItemIds = new Set(menuItemIds)

        state.items.forEach(item => {
            if (item.type === 'dish' && categoryIds.has(item.parent_id)) {
                allItemIds.add(item.id)
            }
        })

        // Убираем блюда, исключённые пользователем из выделения категории
        state.excludedItemIds.forEach(id => allItemIds.delete(id))

        const ids = [...allItemIds]
        if (ids.length === 0) {
            return
        }

        const payload = {
            division_id: state.filters.divisionId,
            menu_item_ids: ids,
        }

        try {
            if (disable) {
                await batchSetMenuItemNoPrint(payload)
            } else {
                await batchDeleteMenuItemNoPrint(payload)
            }

            // Оптимистично обновляем флаг на блюдах
            commit('APPLY_NOT_PRINT_FLAG', {
                menuItemIds: ids,
                notPrint: disable,
            })

            // Обновляем список отключённых печатей в state
            const currentSet = new Set(state.printManagement.noPrintItemIds)
            ids.forEach(id => {
                if (disable) {
                    currentSet.add(id)
                } else {
                    currentSet.delete(id)
                }
            })
            commit('SET_NO_PRINT_ITEM_IDS', [...currentSet])

            notificationService.showSuccess(
                disable ? 'Печать отключена' : 'Печать включена'
            )

            dispatch('clearSelection')
        } catch (error) {
            notificationService.showError(error)
            throw error
        }
    },

    async updatePrintLocations({ dispatch, state, commit }, { workshopIds }) {
        const menuItemIds = state.selectedItems
            .filter(item => item.type === 'dish')
            .map(item => item.id)

        // Формируем payload для bulk update
        const items = menuItemIds.map(menuItemId => ({
            menu_item_id: menuItemId,
            cooking_workshop_ids: workshopIds,
        }))

        const payload = {
            division_id: state.filters.divisionId,
            items,
        }

        try {
            await bulkUpdateMenuItemWorkshops(payload)
            notificationService.showSuccess('Места печати обновлены')

            // Обновляем локальные привязки в store
            commit('UPDATE_WORKSHOP_MAPPINGS', { menuItemIds, workshopIds })

            dispatch('clearSelection')
            await dispatch('fetchItemsWithPrintInfo')
        } catch (error) {
            notificationService.showError(error)
            throw error
        }
    },

    applyPrintLocationFilter({ commit, dispatch }, printLocationIds) {
        commit('SET_SELECTED_PRINT_LOCATION_IDS', printLocationIds)
        // fetchItems автоматически обогатит блюда местами печати
        dispatch('fetchItems')
    },
}
